import React, { ReactNode, useEffect, useState } from "react";
import { useWindowSize } from "react-use";
import { useSwipeable } from "react-swipeable";
import { variants } from "utils/animationVariants";
import { AnimatePresence, motion, MotionConfig } from "framer-motion";
import PrevBtn from "components/NextPrev/PrevBtn";
import NextBtn from "components/NextPrev/NextBtn";

export default function MySliderCard({
  className = "",
  itemPerRow = 5,
  data,
  renderItem = () => <div></div>,
  arrowBtnClass = "top-1/3 -translate-y-1/2",
  autoPlayInterval = 2000, // Czas interwału w ms (np. 2000 = 2 sekundy)
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [numberOfItems, setNumberOfitem] = useState(0);
  const [isHovered, setIsHovered] = useState(false); // Stan do kontrolowania pauzy przy hoverze

  const windowWidth = useWindowSize().width;

  // Ustalamy liczbę elementów w zależności od szerokości okna
  useEffect(() => {
    if (windowWidth < 320) {
      return setNumberOfitem(1);
    }
    if (windowWidth < 500) {
      return setNumberOfitem(itemPerRow - 3);
    }
    if (windowWidth < 620) {
      return setNumberOfitem(itemPerRow - 3);
    }
    if (windowWidth < 1024) {
      return setNumberOfitem(itemPerRow - 2);
    }
    if (windowWidth < 1280) {
      return setNumberOfitem(itemPerRow - 1);
    }

    setNumberOfitem(itemPerRow);
  }, [itemPerRow, windowWidth]);

  // Powielamy dane dla efektu "infinity"
  const extendedData = [...data, ...data];

  // Funkcja zmiany indeksu elementu
  function changeItemId(newVal) {
    if (newVal >= extendedData.length) {
      setCurrentIndex(0); // Po osiągnięciu końca, wracamy na początek
    } else if (newVal < 0) {
      setCurrentIndex(extendedData.length - numberOfItems); // Po osiągnięciu początku, idziemy na koniec
    } else {
      setCurrentIndex(newVal);
    }
  }

  // Obsługa swipe
  const handlers = useSwipeable({
    onSwipedLeft: () => {
      changeItemId(currentIndex + 1); // Zmieniamy indeks o +1 w prawo
    },
    onSwipedRight: () => {
      changeItemId(currentIndex - 1); // Zmieniamy indeks o -1 w lewo
    },
    trackMouse: true,
  });

  // Automatyczne przewijanie
  useEffect(() => {
    if (!isHovered) {
      const interval = setInterval(() => {
        setCurrentIndex((prevIndex) => {
          // Sprawdzamy, czy nie jesteśmy na końcu tablicy
          if (prevIndex + numberOfItems >= extendedData.length) {
            return 0; // Jeśli tak, wracamy na początek
          }
          return prevIndex + 1; // Jeśli nie, przechodzimy do następnego elementu
        });
      }, autoPlayInterval);

      // Czyszczenie interwału po zakończeniu komponentu
      return () => clearInterval(interval);
    }
  }, [isHovered, autoPlayInterval, numberOfItems]);

  // Pauza przy najechaniu myszką
  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  if (!numberOfItems) {
    return <div></div>;
  }

  return (
    <div
      className={`nc-MySlider ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <MotionConfig
        transition={{
          x: { type: "spring", stiffness: 300, damping: 30 },
          opacity: { duration: 0.2 },
        }}
      >
        <div className={`relative flow-root`} {...handlers}>
          <div className={`flow-root overflow-hidden`}>
            <motion.ul
              initial={false}
              className="relative whitespace-nowrap"
            >
              <AnimatePresence initial={false} custom={direction}>
                {extendedData
                  .filter((podcast) => podcast.status == 1)
                  .slice(0, extendedData.length)
                  .reverse()
                  .map((item, indx) => (
                    <motion.li
                      className={`relative inline-block xl:px-4 whitespace-normal`}
                      custom={direction}
                      initial={{
                        x: `${(currentIndex - 1) * -100}%`, // Początkowa animacja
                      }}
                      animate={{
                        x: `${currentIndex * -100}%`, // Przewijanie w lewo
                      }}
                      variants={variants(200, 1)}
                      key={indx}
                      style={{
                        width: `calc(100% / ${numberOfItems})`, // Obliczamy szerokość
                      }}
                    >
                      {renderItem(item, indx)}
                    </motion.li>
                  ))}
              </AnimatePresence>
            </motion.ul>
          </div>

          {currentIndex > 0 && (
            <PrevBtn
              onClick={() => changeItemId(currentIndex - 1)}
              className={`w-9 h-9 xl:w-12 xl:h-12 text-lg absolute -left-4 xl:-left-6 z-[1] ${arrowBtnClass}`}
            />
          )}

          {currentIndex + numberOfItems < extendedData.length && (
            <NextBtn
              onClick={() => changeItemId(currentIndex + 1)}
              className={`w-9 h-9 xl:w-12 xl:h-12 text-lg absolute -right-4 xl:-right-6 z-[1] ${arrowBtnClass}`}
            />
          )}
        </div>
      </MotionConfig>
    </div>
  );
}
